import { Component, Input } from '@angular/core';
import {
    AssignFlightLogCommand,
    CreateFlightsFromLogsCommand,
    FlightLogFileDto,
    FlightLogsService,
    FlyFreelyError,
    FlyFreelyLoggingService,
    PreSyncCheckCommand,
    PreSyncCheckFile
} from '@flyfreely-portal-ui/flyfreely';
import {
    fadeInExpandOnEnterAnimation,
    fadeOutCollapseLeftOnLeaveAnimation
} from 'angular-animations';
import FlightLogWithSummary from 'libs/testing/src/lib/flight-log-with-summary.json';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject, from } from 'rxjs';
import { concatMap, debounceTime, takeUntil, tap } from 'rxjs/operators';
import { FlightLogDataService } from './flight-log-data.service';
import { FlightLogWithDuration } from './flight-log-list/flight-log-list-dialogue.component';
import { FlightLogsDialoguesService } from './flight-logs-dialogues.service';
import { findRelevantMissionIds } from './helpers';

// FIXME: imported jsons with enums need to be figured out
// @ts-ignore - ignoring bad type sensing
const flightLog: FlightLogFileWithSummary = FlightLogWithSummary;

function b2h(buffer: any) {
    return Array.prototype.map
        .call(new Uint8Array(buffer), (x: any) =>
            `00${x.toString(16)}`.slice(-2)
        )
        .join('');
}

interface UploadWrapper {
    file: File;
    id: number;
    logId?: number;
    status:
        | 'CHECKING'
        | 'PENDING'
        | 'UPLOADING'
        | 'PROCESSING'
        | 'PROCESSED'
        | 'ASSIGNING'
        | 'ASSIGNED'
        | 'ASSIGNING_FAILED'
        | 'UPLOAD_FAILED'
        | 'PROCESSING_FAILED'
        | 'EXISTING'
        | 'DELETED';
    log?: FlightLogWithDuration;
    error?: string;
}
@Component({
    selector: 'flight-log-uploader',
    template: `
        <div class="modal-header">
            <h3 class="modal-title">Flight Log Uploads</h3>
        </div>
        <div class="modal-body">
            <enhanced-help
                *ngIf="enhancedHelpActive"
                helpTitle="Upload Flight Logs"
                screenName="flight-log-overview"
                componentName="flight-log-uploader"
                [compact]="true"
                [alwaysActive]="true"
            >
            </enhanced-help>
            <div class="horizontal-container">
                <input
                    type="checkbox"
                    [disabled]="isProcessing || !autoAssignLogsEnabled"
                    [checked]="selectAllNone"
                    (change)="selectAll()"
                />
            </div>
            <div *ngFor="let upload of uploads; let i = index">
                <div class="horizontal-container">
                    <div class="fcol-1">
                        <input
                            type="checkbox"
                            [disabled]="
                                upload.status !== 'PROCESSED' || isProcessing
                            "
                            [checked]="autoAssignSelections[i]"
                            (change)="updateSelections($event, i)"
                        />
                    </div>
                    <div class="fcol-7">
                        <i
                            class="fas fa-circle"
                            *ngIf="
                                upload.status == 'PENDING' ||
                                upload.status == 'CHECKING'
                            "
                        ></i>
                        <i
                            class="fas fa-circle-notch fa-spin"
                            *ngIf="
                                upload.status == 'UPLOADING' ||
                                upload.status == 'PROCESSING' ||
                                upload.status == 'ASSIGNING'
                            "
                        ></i>
                        <i
                            class="fas fa-check"
                            *ngIf="
                                upload.status == 'PROCESSED' ||
                                upload.status === 'ASSIGNED'
                            "
                        ></i>
                        <i
                            class="fal fa-exclamation-circle"
                            *ngIf="
                                upload.status == 'EXISTING' ||
                                upload.status == 'DELETED'
                            "
                        ></i>
                        <i
                            class="fas fa-times"
                            *ngIf="
                                upload.status == 'UPLOAD_FAILED' ||
                                upload.status == 'PROCESSING_FAILED'
                            "
                        ></i>
                        {{ upload.file.name }}
                    </div>
                    <div class="fcol-4" [ngSwitch]="upload.status">
                        <span *ngSwitchCase="'CHECKING'" class="left-buffer"
                            ><em>Checking...</em></span
                        >
                        <span *ngSwitchCase="'PENDING'" class="left-buffer"
                            ><em>Pending...</em></span
                        >
                        <span *ngSwitchCase="'UPLOADING'" class="left-buffer"
                            ><em>Uploading...</em></span
                        >
                        <span
                            *ngSwitchCase="'UPLOAD_FAILED'"
                            class="text-danger left-buffer"
                            ><em>Upload failed</em> {{ upload.error }}</span
                        >
                        <span *ngSwitchCase="'EXISTING'" class="left-buffer"
                            ><em>Already uploaded</em></span
                        >
                        <span *ngSwitchCase="'DELETED'" class="left-buffer"
                            ><em>Previously deleted.&nbsp;</em>
                            <a
                                *ngIf="
                                    assigningRpaId == null &&
                                    !assignedAnRpaIds.includes(upload.id)
                                "
                                class="left-buffer"
                                (click)="uploadFile(upload)"
                            >
                                Upload again
                            </a>
                        </span>
                        <span *ngSwitchCase="'PROCESSING'" class="left-buffer"
                            ><em>Processing...</em></span
                        >
                        <span
                            *ngSwitchCase="'PROCESSING_FAILED'"
                            class="text-danger left-buffer"
                            ><em>Processing failed</em> {{ upload.error }}</span
                        >
                        <span *ngSwitchCase="'ASSIGNING'" class="left-buffer"
                            ><em>Assigning to mission...</em></span
                        >
                        <span *ngSwitchCase="'ASSIGNED'" class="left-buffer"
                            ><em>Assigned to suggested mission</em></span
                        >
                        <ng-container
                            *ngSwitchCase="'PROCESSED'"
                            [ngSwitch]="canAssignRpa(upload.id)"
                        >
                            <ng-container *ngSwitchCase="false">
                                <span
                                    *ngIf="upload.status !== 'ASSIGNED'"
                                    class="left-buffer"
                                    >Uploaded</span
                                >
                            </ng-container>
                            <ng-container *ngSwitchCase="true">
                                <a
                                    *ngIf="
                                        assigningRpaId == null &&
                                        !assignedAnRpaIds.includes(upload.id)
                                    "
                                    class="left-buffer"
                                    (click)="showAssignRpa(upload.id)"
                                >
                                    Assign RPA
                                </a>
                                <span
                                    *ngIf="
                                        assigningRpaId != null &&
                                        assigningRpaId != upload.id
                                    "
                                    class="left-buffer"
                                >
                                    Assign RPA
                                </span>
                                <span
                                    *ngIf="assigningRpaId == upload.id"
                                    class="left-buffer"
                                >
                                    Assigning RPA
                                </span>
                                <span
                                    *ngIf="assignedAnRpaIds.includes(upload.id)"
                                    class="left-buffer"
                                >
                                    RPA Assigned
                                    <a
                                        *ngIf="assigningRpaId == null"
                                        class="left-buffer"
                                        (click)="showAssignRpa(upload.id)"
                                    >
                                        Change
                                    </a>
                                </span>
                            </ng-container>
                        </ng-container>
                    </div>
                </div>
            </div>
        </div>

        <div class="modal-footer">
            <button
                type="button"
                class="btn btn-primary pull-left"
                (click)="doAutoAssign()"
                [disabled]="
                    !completed || isProcessing || !autoAssignLogsSelected
                "
                tooltip="Assign all selected flight logs to their recommended missions."
                placement="top"
            >
                Apply Automatic Assignments
            </button>
            <button
                type="button"
                class="btn btn-primary"
                (click)="close()"
                [disabled]="!completed || isProcessing"
            >
                Done
            </button>
        </div>
    `,
    styles: [
        `
            :host {
                height: 100%;
            }
        `
    ],
    animations: [
        fadeInExpandOnEnterAnimation(),
        fadeOutCollapseLeftOnLeaveAnimation()
    ],
    providers: [FlightLogDataService]
})
export class FlightLogUploader {
    @Input() organisationId: number;
    @Input() missionId: number;
    @Input() flightId: number;
    @Input() files: File[];
    @Input() enhancedHelpActive = false;

    uploads: UploadWrapper[];
    completed = false;
    autoAssignSelections: { [index: number]: boolean };
    selectAllNone = false;

    assigningRpaId: number;
    assignedAnRpaIds: number[] = [];
    selectedLog: FlightLogWithDuration;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private flightLogService: FlightLogsService,
        private flightLogDataService: FlightLogDataService,
        private flightLogsDialoguesService: FlightLogsDialoguesService,
        private modal: BsModalRef,
        private logging: FlyFreelyLoggingService
    ) {}

    ngOnInit() {
        this.autoAssignSelections = this.files.reduce(
            (acc, f, i) => ({ ...acc, [i]: false }),
            {}
        );
        this.uploads = this.files.map((file, id) => ({
            file,
            id,
            status: 'CHECKING',
            preSyncResult: null
        }));

        this.buildPreSyncCheckFiles().then(preSyncCheckFiles =>
            this.doPreSyncCheck(preSyncCheckFiles)
        );

        this.flightLogDataService.flightLogs$
            .pipe(debounceTime(5 * 1000), takeUntil(this.ngUnsubscribe$))
            .subscribe(logs => {
                if (logs.length > 0) {
                    this.checkProcessed(logs);
                }
                const candidates = this.uploads.filter(
                    u => u.status === 'PROCESSING'
                );
                if (candidates.length === 0) {
                    return;
                }
                this.checkProcessingLogs();
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    get isProcessing() {
        return this.uploads.some(u =>
            [
                'CHECKING',
                'PENDING',
                'UPLOADING',
                'PROCESSING',
                'ASSIGNING'
            ].includes(u.status)
        );
    }

    private processNext() {
        const next = this.uploads.find(u => u.status === 'PENDING');

        if (next == null) {
            this.completed = true;
            return;
        }

        this.uploadFile(next);
    }

    uploadFile(upload: UploadWrapper) {
        upload.status = 'UPLOADING';
        this.flightLogService
            .upload(
                this.organisationId,
                this.missionId,
                this.flightId,
                upload.file
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: log => {
                    upload.status = 'PROCESSING';
                    upload.logId = log.id;
                    this.pollStatus();
                    this.processNext();
                },
                error: (error: FlyFreelyError) => {
                    upload.status = 'UPLOAD_FAILED';
                    upload.error = error.message;
                    this.processNext();
                }
            });
    }

    async buildPreSyncCheckFiles() {
        return Promise.all<PreSyncCheckFile>(
            this.uploads.map(
                async f =>
                    new Promise(resolve => {
                        const fileReader = new FileReader();
                        fileReader.readAsArrayBuffer(f.file);
                        fileReader.onloadend = async (entry: any) => {
                            // output: the sha256 digest hex encoded of the file
                            const hash = b2h(
                                await crypto.subtle.digest(
                                    'SHA-512',
                                    entry.target.result
                                )
                            );
                            resolve({
                                checksum: hash,
                                fileName: f.file.name,
                                fileSize: f.file.size
                            });
                        };
                    })
            )
        );
    }

    doPreSyncCheck(checkFiles: PreSyncCheckFile[]) {
        const command: PreSyncCheckCommand = {
            files: checkFiles,
            includeDeletedFiles: true,
            organisationId: this.organisationId
        };
        this.flightLogService
            .performPreSyncCheck(command)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(result => {
                result.forEach(r => {
                    this.uploads.find(u => u.file.name === r.fileName).status =
                        r.exists && !r.wasDeleted
                            ? 'EXISTING'
                            : r.exists && r.wasDeleted
                            ? 'DELETED'
                            : 'PENDING';
                });
                this.processNext();
            });
    }

    canAssignRpa(id: number): boolean {
        const entity = this.uploads.find(u => u.id === id);
        if (entity == null) {
            return false;
        }
        const log = entity.log;
        if (log == null) {
            return false;
        }
        return log?.rpaId == null && log.summary.rpaSerialNumber != null;
    }

    showAssignRpa(id: number): void {
        const entity = this.uploads.find(u => u.id === id);
        if (entity == null) {
            return;
        }
        this.selectedLog = entity.log;
        if (this.selectedLog == null) {
            return;
        }
        const modal = this.flightLogsDialoguesService.showAssignRpa(
            this.selectedLog,
            this.enhancedHelpActive
        );
        this.assigningRpaId = id;

        modal.content.done
            .pipe(takeUntil(this.ngUnsubscribe$), takeUntil(modal.onHidden))
            .subscribe(() => this.assignedAnRpaIds.push(id));

        modal.onHidden
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.doneAssigning());
    }

    doneAssigning() {
        this.assigningRpaId = null;
        this.selectedLog = null;
    }

    doAutoAssign() {
        const selectedLogs: FlightLogWithDuration[] = Object.keys(
            this.autoAssignSelections
        )
            .filter(key => this.autoAssignSelections[key])
            .reduce((acc, index) => {
                const log: UploadWrapper = this.uploads[index];
                if (
                    log.status !== 'PROCESSED' ||
                    log.logId == null ||
                    log.log.suggestedMissionAssignment == null
                ) {
                    return acc;
                } else {
                    log.status = 'ASSIGNING';
                    return acc.concat(log.log);
                }
            }, []);
        if (selectedLogs.length === 0) {
            return;
        }

        // If the logs have suggested flights, assign them to the missions.
        const logsWithSuggestedFlights = selectedLogs.filter(
            l => l.suggestedMissionAssignment.flight != null
        );
        if (logsWithSuggestedFlights.length > 0) {
            this.assignLogsToMission(logsWithSuggestedFlights);
        }

        // If any logs don't have suggested flights, create a flight for them.
        const logsWithoutSuggestedFlights = selectedLogs.filter(
            l => l.suggestedMissionAssignment.flight == null
        );

        if (logsWithoutSuggestedFlights.length === 0) {
            return;
        }
        this.createFlightsForLogs(logsWithoutSuggestedFlights);
    }

    createFlightsForLogs(selectedLogs: FlightLogWithDuration[]) {
        const groupedByMission = findRelevantMissionIds(selectedLogs);
        // First create flights as needed
        const createFlightCalls: CreateFlightsFromLogsCommand[] = Object.keys(
            groupedByMission
        ).reduce(
            (acc, missionId) =>
                acc.concat({
                    missionId: parseInt(missionId, 10),
                    flightLogIdList: groupedByMission[missionId].map(
                        log => log.id
                    )
                }),
            []
        );
        from(createFlightCalls)
            .pipe(
                concatMap(logsCommand =>
                    this.flightLogService
                        .createFlightsFromLogs(logsCommand)
                        .pipe(
                            tap({
                                next: result =>
                                    logsCommand.flightLogIdList.forEach(logId =>
                                        this.setUploadAssignmentStatus(
                                            logId,
                                            'ASSIGNED'
                                        )
                                    ),
                                error: error =>
                                    logsCommand.flightLogIdList.forEach(logId =>
                                        this.setUploadAssignmentStatus(
                                            logId,
                                            'ASSIGNING_FAILED'
                                        )
                                    )
                            })
                        )
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                complete: () => {
                    this.logging.success(
                        `Created flights for ${createFlightCalls.length} flight logs in their suggested missions.`
                    );
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error creating flights from logs: ${error.message}`
                    );
                }
            });
    }

    assignLogsToMission(selectedLogs: FlightLogWithDuration[]) {
        const assignCommands: [number, AssignFlightLogCommand][] =
            selectedLogs.reduce(
                (acc, log) => [
                    ...acc,
                    [
                        log.id,
                        {
                            missionId: log.suggestedMissionAssignment.missionId,
                            flightId:
                                log.suggestedMissionAssignment.flight.id ??
                                log.flightId
                        }
                    ]
                ],
                [] as [number, AssignFlightLogCommand][]
            );
        from(assignCommands)
            .pipe(
                concatMap(([logId, assignCommand]) =>
                    this.flightLogService
                        .assignFlightLog(logId, assignCommand)
                        .pipe(
                            tap({
                                next: result =>
                                    this.setUploadAssignmentStatus(
                                        logId,
                                        'ASSIGNED'
                                    ),
                                error: error =>
                                    this.setUploadAssignmentStatus(
                                        logId,
                                        'ASSIGNING_FAILED'
                                    )
                            })
                        )
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: updatedFlightLogs => {
                    this.logging.success('Flight logs assigned');
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error assigning flights: ${error.message}`
                    );
                }
            });
    }

    setUploadAssignmentStatus(
        logId: number,
        status: 'ASSIGNED' | 'ASSIGNING_FAILED'
    ) {
        const upload = this.uploads.find(u => u.logId === logId);
        if (upload != null) {
            upload.status = status;
        }
    }

    updateSelections(event: Event, i: number) {
        const isChecked = (event.target as HTMLInputElement).checked;
        this.autoAssignSelections[i] = isChecked;
        this.selectAllNone = this.allNoneSelected();
    }

    selectAll() {
        if (
            !this.selectAllNone // meaning not everything has been selected
        ) {
            //select all
            Object.keys(this.autoAssignSelections).forEach(
                key => (this.autoAssignSelections[key] = true)
            );
        } else {
            // select none
            Object.keys(this.autoAssignSelections).forEach(
                key => (this.autoAssignSelections[key] = false)
            );
        }
        this.selectAllNone = this.allNoneSelected();
    }

    pollStatus() {
        const candidates = this.uploads.filter(u => u.status === 'PROCESSING');
        if (candidates.length === 0) {
            return;
        }
        this.checkProcessingLogs();
    }

    checkProcessingLogs() {
        const candidates = this.uploads.filter(u => u.status === 'PROCESSING');
        const logIds = candidates
            .map(c => (c.log != null ? c.log.id : c.logId))
            .filter(id => id != null);
        if (logIds.length === 0) {
            return;
        }

        // Setting the page length to 100 is arbitrary and can be adjusted as needed.
        this.flightLogDataService.findFlightLogs(0, 100, null, {
            organisationId: this.organisationId,
            idList: logIds,
            flightLogCollectionSourceIdList: null,
            uploadedByIdList: null
        });
    }

    checkProcessed(logs: FlightLogWithDuration[]) {
        logs.forEach(l => {
            const upload = this.uploads.find(
                u => u.log?.id === l.id || u.logId === l.id
            );
            if (
                upload == null ||
                upload.status === 'PROCESSED' ||
                upload.status === 'PROCESSING_FAILED'
            ) {
                return;
            }
            upload.log = l;
            if (
                l.processedStatus === FlightLogFileDto.ProcessedStatus.PROCESSED
            ) {
                upload.status = 'PROCESSED';
            } else if (
                l.processedStatus === FlightLogFileDto.ProcessedStatus.FAILED
            ) {
                upload.status = 'PROCESSING_FAILED';
            }
        });

        const processingList = this.uploads.filter(
            u => u.status === 'PROCESSING'
        );
    }

    allNoneSelected() {
        const keys = Object.keys(this.autoAssignSelections);
        const selected = keys.filter(k => this.autoAssignSelections[k]);
        return selected.length === keys.length;
    }

    close() {
        this.modal.hide();
    }

    get autoAssignLogsSelected() {
        return (
            Object.keys(this.autoAssignSelections).filter(
                k => this.autoAssignSelections[k]
            ).length > 0
        );
    }

    get autoAssignLogsEnabled() {
        return this.uploads.some(upload => upload.status === 'PROCESSED');
    }
}
